/**
 * Company: SPHMedia
 * Description: [Follow topic button]
 */
import TrendingGif from "@assets/animation/trending-topic.gif";
import AddBlueIcon from "@assets/icons/add-blue.svg";
import AddWhiteIcon from "@assets/icons/add-white.svg";
import CheckBlueIcon from "@assets/icons/success-blue.svg";
import CheckWhiteIcon from "@assets/icons/success-white.svg";
import { ReactElement } from "react";
import { twMerge } from "tailwind-merge";

type FollowTopicsButtonProps = {
  label: string;
  topicId: string;
  isFollowing: boolean;
  onFollow: (topicId: string, topicTitle: string) => Promise<void>;
  onUnfollow: (topicId: string, topicTitle: string) => Promise<void>;
  variant?: "primary" | "secondary";
  selected?: boolean;
  unSelect?: (topicId: string, topicTitle: string) => Promise<void>;
  isTrending?: boolean;
  isLoading?: boolean;
};

/**
 * FollowTopicsButton
 * Description: [Button for follow topic on article page]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function FollowTopicsButton({
  label,
  topicId,
  isFollowing,
  onFollow,
  onUnfollow,
  variant = "primary",
  selected = false,
  unSelect,
  isTrending = false,
  isLoading = false,
}: FollowTopicsButtonProps): ReactElement {
  const isPrimary = variant === "primary";
  const baseClasses =
    "flex py-2 px-3 gap-1 items-center rounded-[50px] text-xs font-semibold leading-[150%] border transition-colors duration-200 max-w-[227px] md:max-w-[500px]";

  // Compute style by variant, follow state & selection
  const variantClasses = isPrimary
    ? isFollowing
      ? "bg-blue-400 text-white-100 border-blue-400 border-solid"
      : "text-blue-400 border-blue-400 border-dashed"
    : isFollowing
      ? "bg-lightBlue-100 text-blue-400 border-lightBlue-200 border-solid"
      : "text-white-200 border-white-600 border-dashed";

  // Add visual emphasis when selected
  const selectedClasses = selected
    ? isPrimary
      ? "border-blue-400 bg-lightBlue-100 border-solid"
      : "border-lightBlue-200 bg-blue-400 border-solid"
    : "";

  const iconSrc = isPrimary
    ? isFollowing
      ? CheckWhiteIcon
      : AddBlueIcon
    : isFollowing
      ? CheckBlueIcon
      : AddWhiteIcon;

  return (
    <button
      data-testid={`follow-button-${variant}`}
      disabled={isLoading}
      aria-busy={isLoading}
      aria-live="polite"
      className={twMerge(baseClasses, variantClasses, selectedClasses)}
      onClick={() => {
        if (isLoading) return; // extra safety

        if (isFollowing) {
          onUnfollow(topicId, label).catch((error) => {
            console.error("Failed to unfollow topic", topicId, label, error);
          });
        } else if (selected) {
          unSelect?.(topicId, label).catch((error) => {
            console.error("Failed to unselect topic", topicId, label, error);
          });
        } else {
          onFollow(topicId, label).catch((error) => {
            console.error("Failed to follow topic", topicId, label, error);
          });
        }
      }}
    >
      {isTrending && (
        <img src={TrendingGif} width={20} height={20} alt="trending" />
      )}
      <span className="truncate">{label}</span>
      {isLoading ? (
        <>
          <span
            className="w-4 h-4 border-2 border-blue-400 border-t-white-100 rounded-full animate-spin"
            aria-hidden="true"
          />
          <span className="sr-only" role="status">
            Updating follow status
          </span>
        </>
      ) : (
        <img
          data-testid="follow-icon"
          width={16}
          height={16}
          src={iconSrc}
          alt={isFollowing ? "Successfully-following" : "follow-topic"}
        />
      )}
    </button>
  );
}
